import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Shield } from "lucide-react";
import { SEOHead, buildBreadcrumbSchema } from "../components/SEOHead";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const fadeUp = { hidden: { opacity: 0, y: 30 }, visible: (i = 0) => ({ opacity: 1, y: 0, transition: { delay: i * 0.1, duration: 0.6, ease: "easeOut" } }) };

const DEFAULT_SECTIONS = [
  { heading: "Information We Collect", body: "We collect information you provide directly when creating an account, booking a ride, or contacting us. This includes your name, email, phone number, and payment information. We also collect location data when using the app to provide ride services." },
  { heading: "How We Use Your Information", body: "Your information is used to provide and improve our ride services, process payments, communicate with you, ensure safety, and comply with legal obligations. We never sell your personal information to third parties." },
  { heading: "Data Sharing", body: "We share your information with drivers (to complete rides), payment processors (to process transactions), and as required by law. We do not sell personal data. Business partners may receive aggregated, anonymized data." },
  { heading: "Data Security", body: "We employ industry-standard security measures including encryption, secure servers, and regular security audits to protect your personal information. Access to data is restricted to authorized personnel only." },
  { heading: "Your Rights", body: "You have the right to access, correct, or delete your personal information. You can also opt out of marketing communications at any time. Contact us at support@tripsi.ca for any privacy-related requests." },
  { heading: "Cookies & Tracking", body: "We use cookies and similar technologies to improve your experience, analyze usage, and deliver relevant content. You can manage cookie preferences through your browser settings." },
  { heading: "Children's Privacy", body: "Our services are not intended for users under 18. We do not knowingly collect information from minors. If you believe a minor has provided information, contact us immediately." },
  { heading: "Changes to This Policy", body: "We may update this policy periodically. We will notify you of significant changes via email or through the app. Continued use after changes constitutes acceptance." },
  { heading: "Contact Us", body: "For privacy questions or concerns, contact us at support@tripsi.ca or call +1 647-249-9977." },
];

export default function PrivacyPage() {
  const [sections, setSections] = useState(DEFAULT_SECTIONS);
  const [lastUpdated, setLastUpdated] = useState(null);

  useEffect(() => {
    fetch(`${API}/cms/privacy`).then(r => r.json()).then(data => {
      if (data.content?.sections?.length) {
        setSections(data.content.sections);
      }
      if (data.content?.updated_at) setLastUpdated(data.content.updated_at);
    }).catch(() => {});
  }, []);

  return (
    <div data-testid="privacy-page" className="min-h-screen">
      <SEOHead
        title="Privacy Policy"
        description="Tripsi's privacy policy. Learn how we collect, use, and protect your personal information when using our rideshare services."
        canonical="/privacy"
        jsonLd={buildBreadcrumbSchema([{ name: "Home", url: "/" }, { name: "Privacy Policy", url: "/privacy" }])}
      />
      <section className="relative py-24 md:py-36 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-[#E60000]/5 via-transparent to-transparent" />
        <div className="relative max-w-7xl mx-auto px-6 md:px-8 text-center">
          <motion.div initial="hidden" animate="visible">
            <motion.div variants={fadeUp} custom={0} className="w-14 h-14 bg-[#E60000]/20 flex items-center justify-center mx-auto mb-6">
              <Shield size={24} className="text-[#E60000]" />
            </motion.div>
            <motion.h1 variants={fadeUp} custom={1} className="font-['Outfit'] text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tighter text-white mb-4">Privacy Policy</motion.h1>
            <motion.p variants={fadeUp} custom={2} className="text-[#A3A3A3] text-base md:text-lg max-w-xl mx-auto">Your privacy matters to us. Here's how we handle your data.</motion.p>
            {lastUpdated && <motion.p variants={fadeUp} custom={3} className="text-[#A3A3A3]/60 text-xs mt-4">Last updated: {new Date(lastUpdated).toLocaleDateString()}</motion.p>}
          </motion.div>
        </div>
      </section>

      <section className="pb-24 md:pb-32">
        <div className="max-w-3xl mx-auto px-6 md:px-8 space-y-8">
          {sections.map((sec, i) => (
            <motion.div key={sec.heading || `section-${i}`} initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} custom={i % 3} className="bg-[#141414] border border-white/5 p-6 md:p-8">
              <h2 className="font-['Outfit'] text-lg md:text-xl font-bold text-white mb-4">{sec.heading}</h2>
              <p className="text-[#A3A3A3] text-sm leading-relaxed whitespace-pre-line">{sec.body}</p>
            </motion.div>
          ))}
        </div>
      </section>
    </div>
  );
}
